"use client";

import { Link, useRouter } from "@/i18n/navigation";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import Alert from "@/components/ui/Alert/Alert";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import DiscussionCard from "../../content/DiscussionCard/DiscussionCard";
import { useLocale, useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function CommunityComponent() {
    const locale = useLocale();
    const searchParams = useSearchParams();
    const containerRef = useRef(null);
    const router = useRouter();
    const t = useTranslations();

    const [discussions, setDiscussions] = useState([]);
    const [selectedDiscussions, setSelectedDiscussions] = useState([]);
    const [page, setPage] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [numberOfPages, setNumberOfPages] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [firstLoadingDone, setFirstLoadingDone] = useState(false);
    const [languageChanged, setLanguageChanged] = useState(false);
    const [showSolved, setShowSolved] = useState("all");

    const fetchDiscussions = async (
        tmpPage = null,
        forceCategory = null,
        solved = null,
    ) => {
        if (updating) return;

        setUpdating(true);

        let selectedPage = 1;
        if (tmpPage) {
            selectedPage = tmpPage;
        } else {
            selectedPage = checkPage();
        }

        let tmpSolved = "all";
        if (solved) {
            tmpSolved =
                solved == "all"
                    ? "all"
                    : solved == "solved"
                    ? "solved"
                    : "unsolved";
        } else {
            tmpSolved = checkSolved();
        }

        // Category
        let category = checkCategory();

        const response = await fetch("/api/interface/get/public-discussions", {
            method: "post",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                page: selectedPage,
                categories: [forceCategory ? forceCategory : category],
                solved: tmpSolved,
                locale,
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            console.error(data.message);
            return;
        }

        setDiscussions(data.discussions);
        setSelectedDiscussions(data.discussions);
        setNumberOfPages(parseInt(data.total));

        // Scroll to top
        if (firstLoadingDone) {
            containerRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        setFirstLoadingDone(true);

        setUpdating(false);
    };
    const checkPage = () => {
        let tmpPage = searchParams.get("page") || 1;
        if (tmpPage != page) {
            setPage(parseInt(tmpPage));
        }
        return parseInt(tmpPage);
    };
    const checkSolved = () => {
        let tmpSolved = searchParams.get("solved") || "all";
        if (tmpSolved != showSolved) {
            setShowSolved(tmpSolved);
        }
        return tmpSolved;
    };
    const checkCategory = () => {
        let tmpCategory = searchParams.get("category") || "all";
        setSelectedCategory(tmpCategory);
        return tmpCategory;
    };
    const filterSolved = (show) => {
        if (show === "all") {
            setPage(1);
            setShowSolved("all");
            fetchDiscussions(1, null, "all");
        } else {
            let tmpShow = show === "solved" ? "solved" : "unsolved";
            setPage(1);
            setShowSolved(tmpShow);
            fetchDiscussions(1, null, tmpShow);
        }

        if (show === "all") {
            router.push(`/community`, undefined, {
                shallow: true,
            });
        } else {
            router.push(
                `/community?solved=${
                    show === "solved" ? "solved" : "unsolved"
                }`,
                undefined,
                {
                    shallow: true,
                },
            );
        }
    };

    useEffect(() => {
        if (!firstLoadingDone) return;
        checkPage();
        checkSolved();
        fetchDiscussions();
    }, [searchParams]);

    useEffect(() => {
        checkPage();
        checkSolved();
        fetchDiscussions();
    }, []);

    const filters = [
        {
            value: "all",
            label: t("CommunityComponent_filters_all"),
            icon: "chat-round-line-linear",
        },
        {
            value: "solved",
            label: t("CommunityComponent_filters_solved"),
            icon: "check-circle-linear",
        },
        {
            value: "unsolved",
            label: t("CommunityComponent_filters_unsolved"),
            icon: "record-linear",
        },
    ];

    return (
        <>
            {/* Head */}
            <section className="mb-10 md:mb-12" ref={containerRef}>
                <h1 className="text-4xl md:text-5xl text-gray-900 dark:text-white font-bold font-sourceSans pt-8 md:pt-12">
                    {t("community_title")}
                </h1>
                <p className="text-lg md:text-xl text-gray-600 dark:text-grayDark-400 mt-4 max-w-2xl">
                    {t("community_subtitle")}
                </p>
            </section>

            {/* Filters bar */}
            <section className="mb-8">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                    {filters.map((filter) => {
                        const isActive = showSolved === filter.value;
                        return (
                            <button
                                key={filter.value}
                                onClick={() => filterSolved(filter.value)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                                    isActive
                                        ? "bg-primary text-white"
                                        : "bg-gray-100 dark:bg-grayDark-800 text-gray-700 dark:text-grayDark-300 hover:bg-gray-200 dark:hover:bg-grayDark-700"
                                }`}
                            >
                                <SolarIcon name={filter.icon} className="w-4 h-4" />
                                {filter.label}
                            </button>
                        );
                    })}
                </div>
            </section>

            <hr className="border-gray-200 dark:border-grayDark-800 mb-8" />

            {/* List */}
            <section>
                {firstLoadingDone && !updating ? (
                    <>
                        {selectedDiscussions.length == 0 && (
                            <div className="text-center py-16">
                                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-grayDark-800 flex items-center justify-center">
                                    <SolarIcon name="chat-round-line-linear" className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-600 dark:text-grayDark-400 text-lg">
                                    {t("CommunityComponent_noDiscussions")}
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 gap-5">
                            {selectedDiscussions.map((discussion) => (
                                <DiscussionCard
                                    key={`discussions-${discussion.id}`}
                                    discussion={discussion}
                                />
                            ))}
                        </div>

                        {selectedDiscussions.length != 0 &&
                            numberOfPages > 1 && (
                                <div className="flex justify-center items-center pt-10">
                                    <div className="flex items-center gap-1">
                                        {/* Previous button */}
                                        {page > 1 && (
                                            <Link
                                                href={`/community?page=${page - 1}&solved=${showSolved}`}
                                                className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 text-gray-700 dark:text-grayDark-300 hover:bg-gray-200 dark:hover:bg-grayDark-700 transition-colors"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-5 h-5"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <path d="m15 18-6-6 6-6" />
                                                </svg>
                                            </Link>
                                        )}

                                        {/* Page numbers */}
                                        {[...Array(numberOfPages)].map(
                                            (_, i) => {
                                                const pageNum = i + 1;
                                                // Show first, last, current, and neighbors
                                                if (
                                                    pageNum === 1 ||
                                                    pageNum === numberOfPages ||
                                                    (pageNum >= page - 2 &&
                                                        pageNum <= page + 2)
                                                ) {
                                                    return (
                                                        <Link
                                                            key={pageNum}
                                                            href={`/community?page=${pageNum}&solved=${showSolved}`}
                                                            className={`flex items-center justify-center w-10 h-10 rounded-xl font-medium text-sm transition-colors ${
                                                                pageNum ===
                                                                page
                                                                    ? "bg-primary text-white"
                                                                    : "bg-gray-100 dark:bg-grayDark-800 text-gray-700 dark:text-grayDark-300 hover:bg-gray-200 dark:hover:bg-grayDark-700"
                                                            }`}
                                                        >
                                                            {pageNum}
                                                        </Link>
                                                    );
                                                }
                                                // Show ellipsis
                                                if (
                                                    pageNum === page - 3 ||
                                                    pageNum === page + 3
                                                ) {
                                                    return (
                                                        <span
                                                            key={pageNum}
                                                            className="w-10 h-10 flex items-center justify-center text-gray-400"
                                                        >
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                return null;
                                            },
                                        )}

                                        {/* Next button */}
                                        {page < numberOfPages && (
                                            <Link
                                                href={`/community?page=${page + 1}&solved=${showSolved}`}
                                                className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-100 dark:bg-grayDark-800 text-gray-700 dark:text-grayDark-300 hover:bg-gray-200 dark:hover:bg-grayDark-700 transition-colors"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-5 h-5"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                >
                                                    <path d="m9 18 6-6-6-6" />
                                                </svg>
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            )}
                    </>
                ) : (
                    <div className="grid grid-cols-1 gap-5">
                        {[...Array(5)].map((_, i) => (
                            <Skeleton
                                key={i}
                                height={160}
                                style={{ borderRadius: "1rem" }}
                            />
                        ))}
                    </div>
                )}
            </section>
        </>
    );
}
